// import { getApiUrl } from '@/config/api';  // @ resolves to src/
import { getApiUrl } from '../../config/api';
import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/base/Input';
import Button from '../../components/base/Button';
import API_BASE_URL from '../../config/api';
import Logo from '../../../Royaltech_logo.png';
import { AuthContext } from '../../context/AuthContext';


export default function Login() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [popup, setPopup] = useState<{ show: boolean; type: 'success' | 'error' | 'network'; message: string }>({
    show: false,
    type: 'success',
    message: '',
  });
  const [retryCountdown, setRetryCountdown] = useState(60);

  const showPopup = (type: 'success' | 'error' | 'network', message: string) => {
    if (type === 'network') {
      setRetryCountdown(60);
    }
    setPopup({ show: true, type, message });
  };

  useEffect(() => {
    if (!popup.show || popup.type !== 'network' || retryCountdown <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setRetryCountdown((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [popup.show, popup.type, retryCountdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username || !companyId || !password) {
      const msg = 'Please enter username, company ID and password';
      setError(msg);
      showPopup('error', msg);
      return;
    }

    setIsLoading(true);
    try {
      const resp = await fetch(getApiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, company_id: companyId, password })
      });
      
      // Check if response is ok before parsing JSON
      if (!resp.ok) {
        // Try to parse error message
        try {
          const errorData = await resp.json();
          const msg = errorData.error || `Login failed (${resp.status})`;
          setError(msg);
          showPopup('error', msg);
        } catch {
          const msg = `Login failed: ${resp.status} ${resp.statusText}`;
          setError(msg);
          showPopup('error', msg);
        }
        return;
      }
      
      const data = await resp.json();
      if (!data.success) {
        const msg = data.error || 'Login failed';
        setError(msg);
        showPopup('error', msg);
        return;
      }

      localStorage.setItem('userRole', data.role || 'user');
      localStorage.setItem('username', data.username || username);
      localStorage.setItem('token', data.token || 'dummy');
      
      // Dispatch custom event to notify AuthContext of login
      window.dispatchEvent(new CustomEvent('auth-login', { detail: { username: data.username || username } }));
      
      showPopup('success', 'Login successful');
      setTimeout(() => navigate('/home'), 1200);
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const msg = `Network error: ${errorMessage}. Please refresh the page and try again.`;
      setError(msg);
      showPopup('network', msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <div className="absolute top-6 left-6">
        <img 
          // src="https://static.readdy.ai/image/2ec7dc130a52c62101bb6f32cc87babe/d99b972dc62f270b2347c4c50836a1b4.png" 
          // alt="Docker Vision" 
          src={Logo} 
          alt="Royal Tech" 
          className="h-12"
        />
      </div>
      
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Document Parser Extractor</h1>
            <div className="w-full h-px bg-gray-300 mt-4"></div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                label="Username"
              />
              
              <Input
                type="text"
                placeholder="Enter company ID"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                label="Company ID"
              />
              
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                label="Password"
              />
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                <span className="flex items-center justify-center gap-2">
                  {isLoading && (
                    <span
                      className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"
                      aria-hidden="true"
                    />
                  )}
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </span>
              </Button>
              {error && !popup.show && (<p className="text-red-600 text-sm text-center mt-2">{error}</p>)}
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/signup')}
                  className="text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer"
                >
                  Don't have an account? Sign up
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {popup.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full text-center">
            <p className={`text-lg font-semibold mb-2 ${popup.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {popup.type === 'success' ? 'Login Successful' : 'Login Failed'}
            </p>
            <p className="text-gray-600 text-sm mb-4">
              {popup.type === 'network'
                ? 'Network error: Failed to fetch. Please refresh the page and try again.'
                : popup.message}
            </p>
            {popup.type === 'error' && (
              <button
                type="button"
                onClick={() => setPopup((prev) => ({ ...prev, show: false }))}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 cursor-pointer"
              >
                OK
              </button>
            )}
            {popup.type === 'network' && (
              <button
                type="button"
                disabled={retryCountdown > 0}
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {retryCountdown > 0
                  ? `Refresh & Try Again (in ${retryCountdown}s)`
                  : 'Refresh & Try Again'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

